import { ReactElement, useContext } from 'react';

import { useMediaPlayerContext } from '@/components/app/contentPlayer/MediaPlayerProvider';
import { usePacksContext } from '@/components/app/layout/PacksProvider';
import { AuthModalContext } from '@/context/AuthModalContextProvider';
import { useGuideInfo } from '@/hooks/app/useGuideInfo';
import { useGuidePlay } from '@/hooks/app/useGuidePlay';
import { useUserState } from '@/hooks/store';
import { useRouterPush } from '@/hooks/utils/useRouterPush';

import { ClickableWrapper, Duration, PlayIcon, Subtitle, Title, Wrapper } from './styles';
import { MasterclassGuideRowProps } from './types';

const MasterclassGuideRow = ({ guide, program }: MasterclassGuideRowProps): ReactElement => {
	const user = useUserState();
	const routerPush = useRouterPush();
	const onGuidePlay = useGuidePlay();
	const { currentGuide } = usePacksContext();
	const { hideControls } = useMediaPlayerContext();
	const { setIsModalOpen } = useContext(AuthModalContext);
	const { title, subtitle, duration } = useGuideInfo(guide, program);

	const isGuidePlaying = currentGuide?.id === guide.id;

	const onClick = async () => {
		if (!user) {
			setIsModalOpen(true);
			return;
		}

		if (isGuidePlaying) {
			return await routerPush(`/app/player/${guide.id}`);
		}

		return await onGuidePlay(guide.id, program);
	};

	return (
		<Wrapper>
			<ClickableWrapper isDisabled={hideControls} onClick={onClick} data-testid="masterclass-click-wrapper">
				<Title $isPlaying={isGuidePlaying}>{title}</Title>
				{subtitle && <Subtitle>{subtitle}</Subtitle>}
				{duration && <Duration>{duration}</Duration>}
			</ClickableWrapper>
			<PlayIcon aria-hidden data-testid="play-icon" />
		</Wrapper>
	);
};

export default MasterclassGuideRow;
